import React, { useEffect, useMemo } from "react";
import { Table } from "@tanstack/react-table";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PureDropDown, { DropdownOption } from "components/pure-dropdown";

export type FilterOption = {
  value: string;
  label: string;
};

interface FilterDropdownProps {
  column: any;
  columnId: string;
  table: Table<any>;
  showLabel?: boolean;
  options?: FilterOption[];
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({
  column,
  columnId,
  table,
  showLabel = true,
  options = [],
}) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get filter value from URL parameters
  const filterValue = searchParams.get(`filter_${columnId}`) || "";
  const selectedValues = useMemo(() => {
    return filterValue ? filterValue.split(",") : [];
  }, [filterValue]);

  // Sync URL params with table column filters on mount
  useEffect(() => {
    if (selectedValues.length > 0) {
      column.setFilterValue(selectedValues);
    }
  }, [column, selectedValues]); // Include dependencies

  // Get header text
  const getHeaderText = (header: any): string => {
    if (typeof header === "string") {
      return header;
    }
    if (typeof header === "function") {
      try {
        const result = header();
        if (
          result &&
          typeof result === "object" &&
          result.props &&
          result.props.children
        ) {
          return result.props.children;
        }
      } catch (e) {
        // Fallback if function call fails
      }
    }
    return columnId;
  };

  const headerText = getHeaderText(column.columnDef.header);

  // Convert FilterOption[] to DropdownOption[]
  const dropdownOptions: DropdownOption[] = options.map((option) => ({
    value: option.value,
    label: option.label,
  }));

  const handleFilterChange = (value: string | string[]) => {
    const newValues = Array.isArray(value) ? value : [value];

    // Update table column filter
    if (newValues.length > 0) {
      column.setFilterValue(newValues);
    } else {
      column.setFilterValue(undefined);
    }

    // Update URL parameters
    const newParams = new URLSearchParams(searchParams);
    const filterParam = newValues.length > 0 ? newValues.join(",") : "";

    if (filterParam) {
      newParams.set(`filter_${columnId}`, filterParam);
    } else {
      newParams.delete(`filter_${columnId}`);
    }

    setSearchParams(newParams, { replace: true });
  };

  const handleClear = () => {
    // Clear table column filter
    column.setFilterValue(undefined);

    // Clear URL parameter
    const newParams = new URLSearchParams(searchParams);
    newParams.delete(`filter_${columnId}`);
    setSearchParams(newParams, { replace: true });
  };

  return (
    <PureDropDown
      options={dropdownOptions}
      value={selectedValues}
      onChange={handleFilterChange}
      placeholder={t("common.all")}
      label={showLabel ? headerText : undefined}
      multiple={true}
      showClearButton={true}
      onClear={handleClear}
      className=""
    />
  );
};

export default FilterDropdown;
